import { create } from 'zustand';
import { createJSONStorage, devtools, persist, } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createFieldsSlice } from './FormFieldsSlice/formFieldsSlice';
import { FormFieldsSlice } from './FormFieldsSlice/formFieldsTypes';
import { FormImagesSlice, NewImage } from './FormImagesSlice/formImagesTypes';
import { createImagesSlice } from './FormImagesSlice/useFormImagesSlice';
import { createNavigationSlice } from './NavigationDataSlice/NavigationDataSlice';
import { NavigationDataSlice } from './NavigationDataSlice/NavigationDataTypes';
import { logImageRehydrationFailed, logImageMissingData } from 'src/utils/observability/logs/webview-reload/webviewReloadLogs';
import { safeSessionStorage } from './safeSessionStorage';
import { indexedDBUtils } from './safeIndexedDB';

type UploadProductState = FormImagesSlice & FormFieldsSlice & NavigationDataSlice;

const revokeOldBlobUrls = (state: UploadProductState) => {
  state.images.forEach((image) => {
    if (image.type === 'new') {
      const content = image.content as NewImage;
      if (content.url?.startsWith('blob:')) {
        URL.revokeObjectURL(content.url);
      }
    }
  });
};

const rehydrateNewImages = async (state: UploadProductState) => {
  const rehydratedImages: typeof state.images = [];

  for (const image of state.images) {
    if (image.type !== 'new') {
      rehydratedImages.push(image);
      continue;
    }

    const content = image.content as NewImage;

    if (content.blobKey && content.fileName && content.mimeType) {
      try {
        const blob = await indexedDBUtils.getBlob(content.blobKey);
        if (blob) {
          const file = new File([blob], content.fileName, { type: content.mimeType });
          const url = URL.createObjectURL(file);
          rehydratedImages.push({
            ...image,
            content: { ...content, file, url },
          });
        } else {
          logImageRehydrationFailed(content.fileName || 'unknown', 'blob_not_found_in_indexeddb');
          if (content.file && content.url) {
            rehydratedImages.push(image);
          } else {
            logImageRehydrationFailed(content.fileName || 'unknown', 'no_blob_no_fallback');
          }
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        logImageRehydrationFailed(content.fileName || 'unknown', `rehydration_error: ${errorMsg}`);
        if (content.file && content.url) {
          rehydratedImages.push(image);
        }
      }
    } else if (content.file && content.url) {
      rehydratedImages.push(image);
    } else {
      logImageMissingData(content.fileName || 'unknown');
    }
  }

  state.images = rehydratedImages;
};

export const useUploadProductStore = create(
  devtools(
    persist(
      immer<UploadProductState>(
        (...a) => ({
          ...createImagesSlice(...a),
          ...createFieldsSlice(...a),
          ...createNavigationSlice(...a),
          })
        ),
        {
          name: 'uploadProductFormFields',
          storage: createJSONStorage(() => safeSessionStorage),
          partialize: (state) => {
            return {
              ...state,
              images: state.images.map((image) => {
                if (image.type === 'new') {
                  const { file, originalFile, url, dataUrl, ...rest } = image.content as NewImage;
                  // Only keep dataUrl if we don't have blobKey (fallback)
                  const shouldKeepDataUrl = !rest.blobKey;
                  return {
                    ...image,
                    content: {
                      ...rest,
                      ...(shouldKeepDataUrl && dataUrl ? { dataUrl } : {})
                    }
                  };
                }
                return image;
              }),
            };
          },
          onRehydrateStorage: () => async (state) => {
            if (state) {
              revokeOldBlobUrls(state);
              await rehydrateNewImages(state);
            }
          },
        },
    ),
  )
)